import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { BrushBehavior, D3BrushEvent, ScaleLinear, brushX, select } from 'd3';

import Theme from '@src/theme';

import usePreviousValue from '@hooks/common/usePreviousValue';

import {
  OffScreenHandle,
  brushHandleAccentPath,
  brushHandlePath,
} from '../../constants/svg';

import styled from 'styled-components';

const Handle = styled.path<{ color: string | undefined }>`
  cursor: ew-resize;
  pointer-events: none;

  stroke-width: 3;
  stroke: ${({ color }) => color};
  fill: ${({ color }) => color};
`;

const HandleAccent = styled.path`
  cursor: ew-resize;
  pointer-events: none;

  stroke-width: 1.5;
  stroke: ${Theme.background.b1};
  opacity: ${Theme.background.b1};
`;

const LabelGroup = styled.g<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? '1' : '0')};
  transition: opacity 300ms;
`;

const TooltipBackground = styled.rect`
  fill: ${Theme.primary.p3};
`;

const Tooltip = styled.text`
  text-anchor: middle;
  font-size: 13px;
  fill: ${Theme.primary.p2};
`;

// flips the handles draggers when close to the container edges
const FLIP_HANDLE_THRESHOLD_PX = 20;

// margin to prevent tick snapping from putting the brush off screen
const BRUSH_EXTENT_MARGIN_PX = 2;

/**
 * Returns true if every element in `a` maps to the
 * same pixel coordinate as elements in `b`
 */
const compare = (
  a: [number, number],
  b: [number, number],
  xScale: ScaleLinear<number, number>,
): boolean => {
  // normalize pixels to 1 decimals
  const aNorm = a.map((x) => xScale(x).toFixed(1));
  const bNorm = b.map((x) => xScale(x).toFixed(1));
  return aNorm.every((v, i) => v === bNorm[i]);
};

interface Props {
  id: string;
  xScale: ScaleLinear<number, number>;
  interactive: boolean;
  brushLabelValue: (d: 'w' | 'e', x: number) => string;
  brushExtent: [number, number];
  setBrushExtent: (extent: [number, number], mode: string | undefined) => void;
  innerWidth: number;
  innerHeight: number;
}

const Brush: FC<Props> = ({
  id,
  xScale,
  interactive,
  brushLabelValue,
  brushExtent,
  setBrushExtent,
  innerWidth,
  innerHeight,
}) => {
  const brushRef = useRef<SVGGElement | null>(null);
  const brushBehavior = useRef<BrushBehavior<SVGGElement> | null>(null);

  // only used to drag the handles on brush for performance
  const [localBrushExtent, setLocalBrushExtent] = useState<
    [number, number] | null
  >(brushExtent);
  const [showLabels, setShowLabels] = useState(false);
  const [hovering, setHovering] = useState(false);

  const previousBrushExtent = usePreviousValue(brushExtent);

  const brushed = useCallback(
    (event: D3BrushEvent<unknown>) => {
      const { type, selection } = event;

      if (!selection) {
        setLocalBrushExtent(null);
        return;
      }

      const scaled = (selection as [number, number]).map(xScale.invert) as [
        number,
        number,
      ];
      // avoid infinite render loop by checking for change
      if (type === 'end' && !compare(brushExtent, scaled, xScale)) {
        const isUser =
          event.sourceEvent?.type === 'mousemove' ||
          event.sourceEvent?.type === 'mouseup';
        setBrushExtent(scaled, isUser ? 'user' : 'auto');
      }

      setLocalBrushExtent(scaled);
    },
    [xScale, brushExtent, setBrushExtent],
  );

  // keep local and external brush extent in sync
  // i.e. snap to ticks on bruhs end
  useEffect(() => {
    setLocalBrushExtent(brushExtent);
  }, [brushExtent]);

  // initialize the brush
  useEffect(() => {
    if (!brushRef || !brushRef.current) return;

    brushBehavior.current = brushX<SVGGElement>()
      .extent([
        [Math.max(0 + BRUSH_EXTENT_MARGIN_PX, xScale(0)), 0],
        [innerWidth - BRUSH_EXTENT_MARGIN_PX, innerHeight],
      ])
      .handleSize(30)
      .filter(() => interactive)
      .on('brush end', brushed);

    brushBehavior.current(select(brushRef.current));

    if (
      previousBrushExtent &&
      compare(brushExtent, previousBrushExtent, xScale)
    ) {
      select(brushRef.current)
        .transition()
        // @ts-ignore
        .call(brushBehavior?.current.move, brushExtent.map(xScale));
    }

    // brush linear gradient
    select(brushRef.current)
      .selectAll('.selection')
      .attr('stroke', 'none')
      .attr('fill-opacity', '0.1')
      .attr('fill', `url(#${id}-gradient-selection)`);
  }, [
    brushExtent,
    brushed,
    id,
    innerHeight,
    innerWidth,
    interactive,
    previousBrushExtent,
    xScale,
  ]);

  // respond to xScale changes only
  useEffect(() => {
    if (!brushRef.current || !brushBehavior.current) return;
    brushBehavior.current.move(
      select(brushRef.current),
      // @ts-ignore
      brushExtent.map(xScale),
    );
  }, [brushExtent, xScale]);

  // show labels when local brush changes
  useEffect(() => {
    setShowLabels(true);
    const timeout = setTimeout(() => setShowLabels(false), 1500);
    return () => clearTimeout(timeout);
  }, [localBrushExtent]);

  // variables to help render the SVGs
  const flipWestHandle =
    localBrushExtent && xScale(localBrushExtent[0]) > FLIP_HANDLE_THRESHOLD_PX;
  const flipEastHandle =
    localBrushExtent &&
    xScale(localBrushExtent[1]) > innerWidth - FLIP_HANDLE_THRESHOLD_PX;

  const showWestArrow =
    localBrushExtent &&
    (xScale(localBrushExtent[0]) < 0 || xScale(localBrushExtent[1]) < 0);
  const showEastArrow =
    localBrushExtent &&
    (xScale(localBrushExtent[0]) > innerWidth ||
      xScale(localBrushExtent[1]) > innerWidth);

  const westHandleInView =
    localBrushExtent &&
    xScale(localBrushExtent[0]) >= 0 &&
    xScale(localBrushExtent[0]) <= innerWidth;
  const eastHandleInView =
    localBrushExtent &&
    xScale(localBrushExtent[1]) >= 0 &&
    xScale(localBrushExtent[1]) <= innerWidth;

  return (
    <>
      <g
        ref={brushRef}
        clipPath={`url(#${id}-brush-clip)`}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      />

      {/* custom brush handles */}
      {localBrushExtent && (
        <>
          {/* west handle */}
          {westHandleInView ? (
            <g
              transform={`translate(${Math.max(0, xScale(localBrushExtent[0]))}, 0), scale(${flipWestHandle ? '-1' : '1'}, 1)`}
            >
              <g>
                <Handle
                  color={Theme.primary.p3 as string}
                  d={brushHandlePath(innerHeight)}
                />
                <HandleAccent d={brushHandleAccentPath()} />
              </g>

              <LabelGroup
                transform={`translate(50,0), scale(${flipWestHandle ? '1' : '-1'}, 1)`}
                visible={showLabels || hovering}
              >
                <TooltipBackground
                  y="0"
                  x="-30"
                  height="30"
                  width="60"
                  rx="8"
                />
                <Tooltip
                  transform="scale(-1, 1)"
                  y="15"
                  dominantBaseline="middle"
                >
                  {brushLabelValue('w', localBrushExtent[0])}
                </Tooltip>
              </LabelGroup>
            </g>
          ) : null}

          {/* east handle */}
          {eastHandleInView ? (
            <g
              transform={`translate(${xScale(localBrushExtent[1])}, 0), scale(${flipEastHandle ? '-1' : '1'}, 1)`}
            >
              <g>
                <Handle
                  color={Theme.primary.p3}
                  d={brushHandlePath(innerHeight)}
                />
                <HandleAccent d={brushHandleAccentPath()} />
              </g>

              <LabelGroup
                transform={`translate(50,0), scale(${flipEastHandle ? '-1' : '1'}, 1)`}
                visible={showLabels || hovering}
              >
                <TooltipBackground
                  y="0"
                  x="-30"
                  height="30"
                  width="60"
                  rx="8"
                />
                <Tooltip y="15" dominantBaseline="middle">
                  {brushLabelValue('e', localBrushExtent[1])}
                </Tooltip>
              </LabelGroup>
            </g>
          ) : null}

          {showWestArrow && <OffScreenHandle color={Theme.primary.p2} />}

          {showEastArrow && (
            <g transform={`translate(${innerWidth}, 0) scale(-1, 1)`}>
              <OffScreenHandle color={Theme.primary.p2} />
            </g>
          )}
        </>
      )}
    </>
  );
};

export default Brush;
